import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { packagesAPI } from '../../services/api';
import { Card, Button } from '../ui';
import PackageFormModal from './PackageFormModal';
import { handleApiError, showToast, showConfirm, formatDate } from '../../utils/helpers';
import { PlatformIcon } from '../icons/PlatformIcons';
import { PLATFORMS } from '../../utils/platformConfig';
import { Package, Layers } from 'lucide-react';

const PackagesListPage = () => {
  const { isRTL } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await packagesAPI.getAll();
      setPackages(response.data.packages || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const handleCreate = () => {
    setEditingPackage(null);
    setShowModal(true);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من حذف هذه الباقة؟' : 'Are you sure you want to delete this package?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await packagesAPI.delete(id);
      fetchPackages();
      showToast.success(isRTL ? 'تم حذف الباقة بنجاح!' : 'Package deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingPackage(null);
    fetchPackages();
  };

  const getPlatformLabel = (platform) => {
    const platforms = {
      instagram: { en: 'Instagram', ar: 'إنستقرام', icon: '📸' },
      tiktok: { en: 'TikTok', ar: 'تيك توك', icon: '🎵' },
      snapchat: { en: 'Snapchat', ar: 'سناب شات', icon: '👻' },
      twitter: { en: 'Twitter/X', ar: 'تويتر', icon: '🐦' },
      facebook: { en: 'Facebook', ar: 'فيسبوك', icon: '📘' },
      linkedin: { en: 'LinkedIn', ar: 'لينكد إن', icon: '💼' },
      website: { en: 'Website', ar: 'موقع', icon: '🌐' },
      blog: { en: 'Blog', ar: 'مدونة', icon: '📝' },
      all: { en: 'All Platforms', ar: 'جميع المنصات', icon: '🌍' },
    };
    const p = platforms[platform] || { en: platform, ar: platform, icon: '📱' };
    return `${p.icon} ${isRTL ? p.ar : p.en}`;
  };

  const getContentTypeLabel = (contentType) => {
    const types = {
      image_post: { en: 'Image Post', ar: 'بوست صورة' },
      video_post: { en: 'Video Post', ar: 'بوست فيديو' },
      reel: { en: 'Reel', ar: 'ريلز' },
      story: { en: 'Story', ar: 'ستوري' },
      carousel: { en: 'Carousel', ar: 'كاروسيل' },
      banner: { en: 'Banner', ar: 'بانر' },
      all: { en: 'All Types', ar: 'جميع الأنواع' },
    };
    const t = types[contentType] || { en: contentType, ar: contentType };
    return isRTL ? t.ar : t.en;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalActive = packages.filter((p) => p.is_active).length;
  const totalInactive = packages.filter((p) => !p.is_active).length;

  const filteredPackages =
    statusFilter === 'all'
      ? packages
      : packages.filter((p) =>
          statusFilter === 'active' ? p.is_active : !p.is_active
        );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="inline-flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4 text-sky-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'باقات العملاء' : 'Client Packages'}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {isRTL ? 'إدارة الباقات' : 'Packages Management'}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {isRTL
                ? 'إنشاء، تعديل، وتفعيل باقات المحتوى للعملاء'
                : 'Create, edit and activate content packages for your clients'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-slate-200">
                📦 {isRTL ? 'إجمالي الباقات' : 'Total packages'} {packages.length}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700">
                ✅ {isRTL ? 'نشطة' : 'Active'} {totalActive}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 border border-rose-100 text-rose-700">
                ⏸ {isRTL ? 'غير نشطة' : 'Inactive'} {totalInactive}
              </span>
            </div>
          </div>
          <div className="flex items-start">
            <Button
              onClick={handleCreate}
              variant="primary"
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
            >
              <Package className="w-4 h-4 mr-2" />
              {isRTL ? 'إضافة باقة' : 'Add Package'}
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Status Filter Pills */}
      <div
        className={`flex flex-wrap gap-2 ${
          isRTL ? 'justify-end' : 'justify-start'
        }`}
      >
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {isRTL ? 'الكل' : 'All'} ({packages.length})
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'active'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {isRTL ? 'نشطة' : 'Active'} ({totalActive})
        </button>
        <button
          onClick={() => setStatusFilter('inactive')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            statusFilter === 'inactive'
              ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {isRTL ? 'غير نشطة' : 'Inactive'} ({totalInactive})
        </button>
      </div>

      {/* Packages Grid */}
      {filteredPackages.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {isRTL ? 'لا توجد باقات' : 'No Packages Yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {isRTL 
              ? 'أنشئ باقتك الأولى لتقديمها لعملائك'
              : 'Create your first package to offer to your clients'}
          </p>
          <Button onClick={handleCreate} variant="primary">
            {isRTL ? 'إنشاء باقة' : 'Create Package'}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPackages.map((pkg) => (
            <Card
              key={pkg.id}
              className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Status Badge */}
              <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    pkg.is_active
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                      : 'bg-slate-50 text-slate-600 border border-slate-200'
                  }`}
                >
                  {pkg.is_active
                    ? isRTL
                      ? 'نشطة'
                      : 'Active'
                    : isRTL
                    ? 'غير نشطة'
                    : 'Inactive'}
                </span>
              </div>

              {/* Package Info */}
              <div className="mb-4">
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  {isRTL && pkg.name_ar ? pkg.name_ar : pkg.name}
                </h3>
                <p className="text-xs text-slate-500 line-clamp-2">
                  {isRTL && pkg.description_ar
                    ? pkg.description_ar
                    : pkg.description || (isRTL ? 'بدون وصف' : 'No description')}
                </p>
              </div>

              {/* Stats */}
              <div className="space-y-3 mb-4 flex-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    {isRTL ? 'عدد البوستات' : 'Posts limit'}
                  </span>
                  <span className="font-semibold text-sky-600">
                    {pkg.post_limit}
                  </span>
                </div>
                
                {/* Platforms - icons only */}
                {(pkg.platforms && Array.isArray(pkg.platforms) && pkg.platforms.length > 0) ? (
                  <div className="text-sm">
                    <span className="text-slate-500 block mb-2">
                      {isRTL ? 'المنصات' : 'Platforms'}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {pkg.platforms.map((platform, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100"
                        >
                          <PlatformIcon platformId={platform} className="w-4 h-4" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : pkg.platform && pkg.platform !== 'all' ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {isRTL ? 'المنصة' : 'Platform'}
                    </span>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100">
                        <PlatformIcon platformId={pkg.platform} className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                ) : null}
                
                {pkg.content_type && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {isRTL ? 'نوع المحتوى' : 'Content type'}
                    </span>
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-50 text-slate-700 border border-slate-100">
                      {getContentTypeLabel(pkg.content_type)}
                    </span>
                  </div>
                )}

                {pkg.duration_days && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {isRTL ? 'المدة' : 'Duration'}
                    </span>
                    <span className="font-medium">
                      {pkg.duration_days} {isRTL ? 'يوم' : 'days'}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    {isRTL ? 'السعر' : 'Price'}
                  </span>
                  <span className="font-semibold text-emerald-600">
                    {pkg.price > 0 
                      ? `${pkg.price} ${pkg.currency || 'SAR'}`
                      : (isRTL ? 'مجاني' : 'Free')}
                  </span>
                </div>
              </div>

              {/* Subscriptions */}
              <div className="py-3 border-t border-slate-200 space-y-2 mt-auto">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    {isRTL ? 'الاشتراكات النشطة' : 'Active subscriptions'}
                  </span>
                  <span className="font-semibold text-emerald-600">
                    {pkg.active_subscriptions || 0}
                  </span>
                </div>
                {pkg.total_subscriptions !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {isRTL ? 'إجمالي الاشتراكات' : 'Total subscriptions'}
                    </span>
                    <span className="font-semibold text-slate-700">
                      {pkg.total_subscriptions || 0}
                    </span>
                  </div>
                )}
                {pkg.created_at && (
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                    <span className="text-slate-500">
                      {isRTL ? 'تاريخ الإنشاء' : 'Created'}
                    </span>
                    <span className="text-slate-600 text-xs">
                      {formatDate(pkg.created_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div
                className={`flex items-center pt-3 border-t border-slate-200 mt-2 ${
                  isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                }`}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEdit(pkg)}
                  className="flex-1"
                >
                  {isRTL ? 'تعديل' : 'Edit'}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(pkg.id)}
                  className="flex-1"
                >
                  {isRTL ? 'حذف' : 'Delete'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <PackageFormModal
          package={editingPackage}
          onClose={() => {
            setShowModal(false);
            setEditingPackage(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default PackagesListPage;






